import React, { useState } from 'react'

import { Box, Button, Grid, Typography } from '@mui/material'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { brainstormText } from 'content/brainstormText'

import ButtonWithIconComponent from 'components/ButtonWithIcon'
import { GeneratedExampleDropdown } from 'components/GeneratedExampleDropdown/GeneratedExampleDropdown'
import { generatedExampleDropdownData } from 'components/GeneratedExampleDropdown/generatedExampleDropdownData'
import InfoPopper from 'components/InfoPopper/InfoPopper'
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import refreshIcon from 'assets/images/refreshIcon.svg'

import { styles as BaseStyles } from '../../LeapFrog.styles'
import { styles as Section1Styles } from '../Section1/Section1.styles'
import { styles } from './Section2.styles'

export const TEST_ID = 'leap-frog-section2'

type Response = {
	ideas: string[]
	title: string
}

type BulletItem = {
	details: string
	title: string
}

type PopperContent = {
	bulletItems: BulletItem[]
	header: string
	introText: string
}

export type Props = {
	currentPrompts: string[]
	currentResponses: Response[]
	generatePromptsDisabled: () => boolean
	handleAiResponseClick: () => void
	handleRandomPrompts: () => void
	isAiLoading: boolean
	isLoading: boolean
	popperContent: PopperContent
}

const Section2 = ({
	isLoading,
	isAiLoading,
	currentPrompts,
	currentResponses,
	generatePromptsDisabled,
	handleRandomPrompts,
	popperContent,
}: Props) => {
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
	const [businessComparisonIndex, setBusinessComparisonIndex] = useState(0)
	const gameId = GAME_TYPE.LEAP_FROG.value as number
	const gameHeroData = generatedExampleDropdownData[gameId]
	const [showAIExampleIdea, setShowAIExampleIdea] = useState(false)

	const handleClickInfoIcon = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(anchorEl ? null : event.currentTarget)
	}

	const listStyle = {
		background: gameHeroData.bg,
		padding: '2em',
		margin: 0,
	}

	const buildResponseItem = (solution: Response[]) => {
		return (
			<ol data-testid="gen-ai-response-custom-list" style={listStyle}>
				{solution.map((el, i) =>
					i === businessComparisonIndex ? (
						<React.Fragment key={i}>
							{el.ideas.map((subel, x) => (
								<Typography
									fontSize={16}
									color="black"
									component="li"
									data-testid="gen-ai-sub-response-list"
									key={`ai-sub-solution-${x}`}
									sx={BaseStyles.solutionText}
									style={{ margin: '10px 0' }}
									variant="body1"
								>
									{subel}
								</Typography>
							))}
						</React.Fragment>
					) : null
				)}
			</ol>
		)
	}

	return (
		<>
			<Section2Title />
			<UserEducationModal />
			<Box display="flex">
				<Box marginRight="2rem" sx={Section1Styles.sideBar} />
				<Box sx={styles.outerContainer}>
					<Box sx={styles.innerContainer}>
						<Box sx={styles.instructionContainer}>
							<Typography sx={styles.instructionText}>
								How can you draw inspiration from each of the
								random elements below, either individually or in
								combination with each other?
							</Typography>
							<InfoPopper
								handleClick={handleClickInfoIcon}
								anchorEl={anchorEl}
								popperContent={popperContent}
							/>
						</Box>
						<ButtonWithIconComponent
							disabled={generatePromptsDisabled() || isLoading}
							iconSvg={refreshIcon}
							text="REFRESH"
							data-testid={`${TEST_ID}__refresh-button`}
							onClickAction={handleRandomPrompts}
						/>
					</Box>
					{isLoading ? (
						<LoadingSpinner />
					) : (
						<Grid item sx={BaseStyles.containerAi}>
							{currentPrompts?.map((value, index) => (
								<Button
									sx={styles.businessComparisonButton(
										businessComparisonIndex === index
									)}
									data-testid={`${TEST_ID}__business-comparison-${index}`}
									key={`${TEST_ID}__business-comparison-key-${index}`}
									className={
										index === businessComparisonIndex
											? 'selected'
											: ''
									}
									onClick={() =>
										setBusinessComparisonIndex(index)
									}
								>
									{value}
								</Button>
							))}
						</Grid>
					)}
					<Typography color="black">
						{brainstormText.global.ideasDropdown}
					</Typography>

					<GeneratedExampleDropdown
						isLoading={isAiLoading}
						gameTypeEnum={GAME_TYPE.LEAP_FROG}
						headerText=""
						customResponse={buildResponseItem(currentResponses)}
						showAIExampleIdea={showAIExampleIdea}
						setShowAIExampleIdea={setShowAIExampleIdea}
					/>
				</Box>
			</Box>
		</>
	)
}

export default Section2
